import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

/**
 * SEO Head Component - Manages meta tags, structured data, and Open Graph tags
 * Usage: <SEOHead title="..." description="..." type="website|product" data={{...}} canonicalUrl="..." />
 */
const SEOHead = ({ 
  title, 
  description, 
  type = 'website',
  image = 'https://aaaofmiami.com/og-image.jpg',
  data = null,
  canonicalUrl = null  // Optional override for canonical URL
}) => {
  const location = useLocation();
  // Always use production URL for canonical tags
  const baseUrl = 'https://aaaofmiami.com';
  // Use provided canonicalUrl or fall back to current pathname
  const currentUrl = canonicalUrl ? `${baseUrl}${canonicalUrl}` : `${baseUrl}${location.pathname}`;
  
  // Default values
  const defaultTitle = 'AAA of Miami | Premium Luxury & Performance Vehicles';
  const defaultDescription = 'Discover 350+ premium luxury and performance vehicles at AAA of Miami. Quality pre-owned cars with detailed inspections. Visit our Miami showroom today.';
  
  const pageTitle = title || defaultTitle;
  const pageDescription = description || defaultDescription;
  
  // Generate structured data based on type
  const generateStructuredData = () => {
    const baseSchema = {
      '@context': 'https://schema.org',
    };
    
    if (type === 'organization') {
      return {
        ...baseSchema,
        '@type': 'AutoDealer',
        'name': 'AAA of Miami',
        'description': 'Premium luxury and performance vehicle dealership',
        'url': baseUrl,
        'logo': `${baseUrl}/logo.png`,
        'address': {
          '@type': 'PostalAddress',
          'streetAddress': 'Miami',
          'addressLocality': 'Miami',
          'addressRegion': 'FL',
          'addressCountry': 'US'
        },
        'contactPoint': {
          '@type': 'ContactPoint',
          'contactType': 'Customer Service',
          'availableLanguage': ['English', 'Spanish']
        },
        'openingHours': 'Mo-Sa 09:00-18:00',
        'priceRange': '$$$$'
      };
    }
    
    if (type === 'product' && data) {
      return {
        ...baseSchema,
        '@type': 'Car',
        'name': `${data.year} ${data.brand} ${data.model}`,
        'description': data.description || `${data.year} ${data.brand} ${data.model} for sale`,
        'brand': {
          '@type': 'Brand',
          'name': data.brand
        },
        'model': data.model,
        'productionDate': data.year?.toString(),
        'mileageFromOdometer': {
          '@type': 'QuantitativeValue',
          'value': data.mileage,
          'unitCode': 'SMI'
        },
        'color': data.color,
        'vehicleIdentificationNumber': data.vin,
        'image': data.images?.[0] || image,
        'offers': {
          '@type': 'Offer',
          'price': data.price,
          'priceCurrency': 'USD',
          'availability': 'https://schema.org/InStock',
          'seller': {
            '@type': 'AutoDealer',
            'name': 'AAA of Miami'
          }
        }
      };
    }
    
    // Default website schema
    return {
      ...baseSchema,
      '@type': 'WebSite',
      'name': 'AAA of Miami',
      'url': baseUrl,
      'potentialAction': {
        '@type': 'SearchAction',
        'target': `${baseUrl}/inventory?search={search_term_string}`,
        'query-input': 'required name=search_term_string'
      }
    };
  };
  
  const structuredData = generateStructuredData();
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content="luxury cars miami, performance vehicles, pre-owned cars, used cars miami, exotic cars, sports cars, AAA motors" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="AAA of Miami" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={image} />
      
      {/* Additional SEO */}
      <link rel="canonical" href={currentUrl} />
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      
      {/* Structured Data (JSON-LD) */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

export default SEOHead;
